/** Shared by the kit's overlays (Sheet, Dialog, CommandPalette): focus return, and a page that is inert behind a modal. No scrim: the reference has none, so a modal surface stands on its shadow and the page behind is inert, not veiled. */
import { useEffect, useRef } from "react";

/**
 * Focus return for a Radix dialog that has no trigger. Spread `onOpenAutoFocus` and `onCloseAutoFocus`
 * on its Content: opening remembers what had focus and moves focus to `first()` (or the surface);
 * closing gives focus to `restore()` or, failing that, back to what opened it.
 */
export function useFocusReturn({ first, restore }: { first?: () => HTMLElement | null; restore?: () => HTMLElement | null } = {}) {
  const opener = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: (event: Event) => {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      event.preventDefault();
      (first?.() ?? (event.currentTarget as HTMLElement | null))?.focus({ preventScroll: true });
    },
    onCloseAutoFocus: (event: Event) => {
      event.preventDefault();
      const target = restore?.() ?? opener.current;
      if (target?.isConnected) target.focus({ preventScroll: true });
    },
  };
}

/**
 * A modal layer (a dialog, a menu) marks the rest of the page `aria-hidden`. This makes the same
 * parts `inert` for as long as they are marked, so nothing a screen reader cannot see can take
 * focus. A modal that is not Radix's (the AppShell drawer, which only toggles its own `inert`) gets
 * the same: while it is open, what stands beside its layer is hidden and inert. Call it once, where
 * the page is mounted.
 */
export function useInertBehindModals(): void {
  useEffect(() => {
    // Only what this made inert is released: a part that was inert already (a collapsed sidebar) stays so.
    const mine = new WeakSet<Element>();
    const hide = (el: Element) => {
      if (!el.hasAttribute("inert")) mine.add(el);
      el.setAttribute("inert", "");
    };
    const release = (el: Element) => {
      if (mine.delete(el)) el.removeAttribute("inert");
    };
    const beside = (modal: Element) => Array.from(modal.parentElement?.parentElement?.children ?? []).filter((el) => el !== modal.parentElement);
    const observer = new MutationObserver((records) => {
      for (const { target, attributeName } of records) {
        if (!(target instanceof Element)) continue;
        if (attributeName === "inert") {
          if (target.getAttribute("aria-modal") !== "true" || target.hasAttribute("data-state")) continue;
          const open = !target.hasAttribute("inert");
          for (const el of beside(target)) {
            if (open) {
              el.setAttribute("aria-hidden", "true");
              hide(el);
            } else if (mine.has(el)) {
              el.removeAttribute("aria-hidden");
              release(el);
            }
          }
        } else if (target.hasAttribute("data-aria-hidden")) hide(target);
        else release(target);
      }
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["data-aria-hidden", "inert"] });
    return () => observer.disconnect();
  }, []);
}

/** Said on the document when the shell's drawer opens (below lg): a sheet open beside it closes, one panel at a time. */
export const DRAWER_OPENS = "keystone:drawer-opens";
